/**
 * Offline queue for service submissions.
 * One pending row per idempotency key (or per existing service id).
 * A second save of the same intent replaces that row.
 */

const STORAGE_KEY_PREFIX = "ecrane_offline_service_";
const SYNC_QUEUE_KEY = "ecrane_sync_queue";
const listeners = new Set();
const heldForEdit = new Set();

export function holdOfflineService(storageKey) {
  if (!storageKey) return () => {};
  heldForEdit.add(storageKey);
  return () => {
    if (!heldForEdit.delete(storageKey)) return;
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("ecrane-offline-services-changed"));
    }
  };
}

export function isOfflineServiceHeld(storageKey) {
  return heldForEdit.has(storageKey);
}

export function subscribeOfflineServices(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emitOfflineServicesChanged() {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch (error) {
      console.error("Offline service listener failed:", error);
    }
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("ecrane-offline-services-changed"));
  }
}

export function serviceIntentId(serviceData) {
  const key = serviceData?.request?.idempotency_key || serviceData?.idempotency_key;
  if (key) return String(key);
  if (serviceData?.service_id) return `service-${serviceData.service_id}`;
  return null;
}

export function offlineStorageKey(serviceData) {
  return storageKeyFor(serviceData);
}

function storageKeyFor(serviceData) {
  const intent = serviceIntentId(serviceData);
  if (intent) return `${STORAGE_KEY_PREFIX}${intent}`;
  return `${STORAGE_KEY_PREFIX}${Date.now()}`;
}

function readRecord(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error("Error reading offline service:", error);
    return null;
  }
}

function dropOtherCopies(storageKey, intent) {
  if (!intent) return 0;
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith(STORAGE_KEY_PREFIX) || key === storageKey) continue;
    const data = readRecord(key);
    if (data && serviceIntentId(data) === intent) keysToRemove.push(key);
  }
  keysToRemove.forEach((key) => {
    localStorage.removeItem(key);
    removeFromSyncQueue(key);
  });
  return keysToRemove.length;
}

export function saveOfflineService(serviceData) {
  const storageKey = storageKeyFor(serviceData);
  const intent = serviceIntentId(serviceData);
  const existing = readRecord(storageKey);
  const dataToStore = {
    ...serviceData,
    timestamp: existing?.timestamp || Date.now(),
    synced: false,
    blocked: Object.prototype.hasOwnProperty.call(serviceData, "blocked")
      ? (serviceData.blocked || null)
      : null,
  };

  try {
    const removed = dropOtherCopies(storageKey, intent);
    localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    addToSyncQueue(storageKey);
    emitOfflineServicesChanged();
    return { storageKey, replaced: Boolean(existing) || removed > 0 };
  } catch (error) {
    console.error("Error saving offline service:", error);
    throw error;
  }
}

export function getOfflineService(storageKey) {
  if (!storageKey) return null;
  return readRecord(storageKey);
}

export function getUnsyncedServices() {
  const unsynced = [];

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        const data = readRecord(key);
        if (data && !data.synced) {
          unsynced.push({ key, ...data });
        }
      }
    }
  } catch (error) {
    console.error("Error getting unsynced services:", error);
  }

  return unsynced.sort((a, b) => a.timestamp - b.timestamp);
}

export function markServiceSynced(storageKey) {
  try {
    localStorage.removeItem(storageKey);
    removeFromSyncQueue(storageKey);
    emitOfflineServicesChanged();
  } catch (error) {
    console.error("Error marking service as synced:", error);
  }
}

export function markServiceBlocked(storageKey, message) {
  const data = readRecord(storageKey);
  if (!data) return;
  data.blocked = message || "The server rejected this service.";
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
    emitOfflineServicesChanged();
  } catch (error) {
    console.error("Error recording offline service failure:", error);
  }
}

export function clearServiceBlocked(storageKey) {
  const data = readRecord(storageKey);
  if (!data || !data.blocked) return;
  data.blocked = null;
  try {
    localStorage.setItem(storageKey, JSON.stringify(data));
    emitOfflineServicesChanged();
  } catch (error) {
    console.error("Error clearing offline service failure:", error);
  }
}

export function clearAllOfflineServices() {
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(STORAGE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    localStorage.removeItem(SYNC_QUEUE_KEY);
    emitOfflineServicesChanged();
  } catch (error) {
    console.error("Error clearing offline services:", error);
  }
}

function addToSyncQueue(storageKey) {
  try {
    const queue = getSyncQueue();
    if (!queue.includes(storageKey)) {
      queue.push(storageKey);
      localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
    }
  } catch (error) {
    console.error("Error adding to sync queue:", error);
  }
}

function removeFromSyncQueue(storageKey) {
  try {
    const queue = getSyncQueue();
    const filtered = queue.filter((key) => key !== storageKey);
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error("Error removing from sync queue:", error);
  }
}

function getSyncQueue() {
  try {
    const queue = localStorage.getItem(SYNC_QUEUE_KEY);
    return queue ? JSON.parse(queue) : [];
  } catch (error) {
    console.error("Error getting sync queue:", error);
    return [];
  }
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}

export function submissionFailureMessage(error) {
  const messages = [];
  const collect = (list) => {
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (item?.message) messages.push(item.message);
    });
  };
  collect(error?.graphQLErrors);
  collect(error?.networkError?.result?.errors);
  if (messages.length) return messages.join(" ");
  const status = error?.networkError?.statusCode || error?.networkError?.status;
  if (status) return `The server could not accept this service (${status}).`;
  return "The server could not accept this service.";
}

export function isConnectivityFailure(error) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (!error) return false;
  if (Array.isArray(error.graphQLErrors) && error.graphQLErrors.length > 0) return false;
  const networkError = error.networkError;
  if (!networkError) {
    return /failed to fetch|network request failed|load failed|timeout|offline/i.test(String(error.message || ""));
  }
  const status = networkError.statusCode || networkError.status;
  if (status) return false;
  return true;
}

export function getUnsyncedCount() {
  return getUnsyncedServices().length;
}

export function pendingServiceSummary(item) {
  const crane = item?.crane_name || (item?.crane_id ? `Crane ${item.crane_id}` : "Service");
  const when = item?.timestamp ? new Date(item.timestamp).toLocaleString() : "";
  const summary = when ? `${crane} · saved ${when}` : crane;
  return item?.blocked ? `${summary} · ${item.blocked}` : summary;
}
